import { Component, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Application } from '@modules/common/types/application';
import { next7DaysArray } from '@modules/common/types/next-7-days';
import { Stitch } from '@modules/common/types/stitch';
import { Tab } from '@modules/common/types/tab';
import { isCreationAllowedInVirtualFolder } from '@modules/common/utils/virtual-folder';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { ProjectListStateService } from '@modules/tasks/services/project-list-state.service';
import { TaskingAppStateService } from '@modules/tasks/services/state.service';
import { TaskListStateService } from '@modules/tasks/services/task-list-state.service';
import { type ListState } from '@modules/tasks/types/list-state';
import { Project } from '@modules/tasks/types/project';
import { ProjectsFilters } from '@modules/tasks/types/projects-filters';
import { Task } from '@modules/tasks/types/task';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { VirtualFolder } from '@modules/tasks/types/virtual-folder';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { TasksTenantScopeService } from '@modules/tasks/services/tasks-tenant-scope.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { SpaceType } from '@sharedlib/common/types/SpaceType';

@Component({
  selector: 'app-tasks-panel',
  templateUrl: './tasks-panel.component.html',
  styleUrls: ['./tasks-panel.component.less'],
  standalone: false,
  providers: [
    ...TaskListStateService.providers({
      stateKey: StateKey.tasksDPListState,
      withAdvancedSearch: true,
    }),
    ...ProjectListStateService.providers({
      stateKey: StateKey.projectsDPListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class TasksPanelComponent implements OnInit, OnDestroy {
  /**
   * Tasking pages that select tasks by due date or priority. The DP's quick form does not seed either
   * from the page the way the main-view sidebar does (see this app's `getCommonParams`), so a task
   * created here would not match the list it was created from. DP-001 hides the form rather than
   * seeding it; switching to seeding would be a product change, not a refactor.
   */
  private static readonly NON_CREATABLE_FOLDERS: ReadonlySet<VirtualFolder> = new Set<VirtualFolder>([
    'overdue',
    'today',
    'week',
    ...next7DaysArray,
    'priority',
    'low',
    'normal',
    'high',
  ]);

  public projectsFilters: ProjectsFilters;
  public tasksFilters: TasksFilters;
  public contextMenuOpened: boolean;
  public selectedFilter: VirtualFolder;
  public listState: ListState;
  public tabs: Tab[] = [
    { title: 'Projects', value: 'projects' },
    { title: 'Tasks', value: 'tasks' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'tasks';

  /**
   * Derived pages offer no creation form — see {@link TasksPanelComponent.NON_CREATABLE_FOLDERS}.
   * Derived from the stored page rather than from `selectFilter`, so it cannot drift from the list.
   */
  protected readonly creationAllowed: Signal<boolean>;

  protected readonly popoverClose = new Subject<void>();
  private alive = new Subject<void>();

  private readonly taskListStateService: TaskListStateService = inject(TaskListStateService, { self: true });
  private readonly projectListStateService: ProjectListStateService = inject(ProjectListStateService, { self: true });
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  protected readonly tasksTenantScope = inject(TasksTenantScopeService);
  private readonly spaceContextStore = inject(SpaceContextStore);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  /** Location badges are shown on task rows only while an advanced search is applied. */
  protected readonly isAdvancedSearchApplied: Signal<boolean>;
  /**
   * Search-shaped: the shared query/markers plus transient ids. Tasking exposes no form-only fields of its
   * own, so there is nothing extra to report here; the date range and `stitched` light the funnel pill instead.
   */
  protected readonly isSearchApplied: Signal<boolean>;

  constructor(
    private dpService: DynamicPanelService,
    private modalService: ModalService,
    private injector: Injector,
    private searchService: AdvancedSearchService,
    private stateService: TaskingAppStateService,
  ) {
    this.isAdvancedSearchApplied = toSignal(this.searchService.isAdvancedSearchApplied(Application.tasking), {
      initialValue: false,
    });
    this.isSearchApplied = toSignal(this.searchService.isSearchApplied(Application.tasking), { initialValue: false });
    this.creationAllowed = toSignal(
      this.stateService
        .getVirtualFolder()
        .pipe(map((folder) => isCreationAllowedInVirtualFolder(folder, TasksPanelComponent.NON_CREATABLE_FOLDERS))),
      { initialValue: true },
    );
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    // Inside a team an untouched panel lists the TEAM's tasks (the prototype's companion).
    const team =
      this.spaceContextStore.type() === SpaceType.SHARED_SPACE ? this.spaceContextStore.contextId() : undefined;
    if (team && this.tasksTenantScope.tenantId() === undefined) {
      this.tasksTenantScope.selectTenant(team);
    }

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
      });

    combineLatest([
      this.projectListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        this.projectsFilters = new ProjectsFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.taskListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        this.tasksFilters = new TasksFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  public handleNewTask(task = new Task()): void {
    task.accountId ??= this.tasksTenantScope.effectiveTenantId() ?? undefined;
    this.modalService.openFullForm(task, this.injector, owningTenantContext(task.accountId));
  }

  openTaskInModal(task: Task) {
    this.modalService.openFullForm(task, this.injector);
  }

  openTask(task: Task) {
    this.dpService.setFormItem(task);
  }

  openProject(project: Project) {
    this.dpService.setFormItem(project);
  }

  selectFilter(folder: VirtualFolder): void {
    this.selectedFilter = folder;
    this.stateService.setVirtualFolder(folder);
  }

  protected handleSearch(): void {
    this.popoverClose.next();
  }

  protected clearSearch(): void {
    // Only the search terms: the per-app date/stitched filters belong to the filters menu and its pill.
    this.searchService.resetSearchTerms();
    // The transient "View Contacts" ids are part of those terms, so the mode they opened ends here —
    // at the owner, independently of whether the folder-menu directive is mounted to notice.
    this.dpService.clearIdsSearchMode();
  }

  protected handleCancel(): void {
    this.popoverClose.next();
  }

  /** "Add details" on the quick form: continue in the full form with what was typed. */
  protected handleNewProject(project: Stitch): void {
    this.modalService.openFullForm(project, this.injector);
  }
}
